/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Heading } from '@msdyn365-commerce-modules/utilities';
import {
  getRichTextHtml,
  IImageSettings,
  Image
} from '@msdyn365-commerce/core';
import * as React from 'react';
import {
  ILinksData,
  IShippingServiceConfig,
  IShippingServiceProps
} from './shipping-service.props.autogenerated';

/**
 *
 * ShippingService component
 * @extends {React.PureComponent<IShippingServiceConfig>}
 */
class ShippingService extends React.PureComponent<
  IShippingServiceProps<IShippingServiceConfig>
> {
  public render(): JSX.Element | null {
    if (!(this.props && this.props.config)) {
      return null;
    }

    const content = this.props.config;
    const lazyloadConfig: boolean | undefined =
      (content && content.lazyload) || false;
    const defaultImageSettings: IImageSettings = {
      viewports: {
        xs: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
        lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
      },
      lazyload: lazyloadConfig
    };
    return (
      <div className='shipping-service'>
        {content.heading && (
          <Heading
            className='shipping-service-heading'
            headingTag={content.heading.tag}
            text={content.heading.text}
          />
        )}
        {content.image && (
          <Image
            className='shipping-service-image'
            {...content.image}
            gridSettings={this.props.context.request.gridSettings!}
            imageSettings={content.image.imageSettings || defaultImageSettings}
          />
        )}
        {content.paragraph && (// tslint:disable-next-line:react-no-dangerous-html
          <div className='shipping-service-text' dangerouslySetInnerHTML={getRichTextHtml(content.paragraph)} />
        )}
        {content.links && content.links.length
          ? content.links.map((cta: ILinksData, index: number) => (
              <a
                key={index}
                className='shipping-service-action-text'
                role='button'
                title={cta.linkText}
                href={cta.linkUrl && cta.linkUrl.destinationUrl}
                aria-label={cta.ariaLabel}
                target={cta.openInNewTab ? 'blank' : undefined}
              >
                {cta.linkText}
              </a>
            ))
          : false}
      </div>
    );
  }
}

export default ShippingService;
